import { EditryClient, getModelEntryData } from '@lazylabs-dev/editry-react';
import { builder as originalBuilder } from '@builder.io/react';
import { getCurrentLanguage } from '../utils/languageSwitcher';
import { BUILDER_PUBLIC_KEY, editryConfig, isEditryConfigured } from './config';

const modelIdBySlug = new Map();
const itemsCache = new Map();
let editryClient;

function getClient() {
  if (!editryClient) {
    editryClient = new EditryClient({
      baseUrl: editryConfig.baseUrl,
      editryToken: editryConfig.editryToken
    });
  }
  return editryClient;
}

const EDITRY_LANGUAGE_BY_LOCALE = {
  'cs-CZ': 'cs',
  'sk-SK': 'sk',
  'pl-PL': 'pl',
  'hu-HU': 'hu',
  cs: 'cs',
  sk: 'sk',
  pl: 'pl',
  hu: 'hu'
};

function toEditryLanguage(locale) {
  if (!locale) {
    return 'cs';
  }
  return EDITRY_LANGUAGE_BY_LOCALE[locale] || String(locale).split('-')[0].toLowerCase();
}

function currentLocale(options = {}) {
  if (options.locale) {
    return options.locale;
  }

  try {
    return getCurrentLanguage();
  } catch {
    return 'cs-CZ';
  }
}

function currentLanguage(options = {}) {
  return toEditryLanguage(currentLocale(options));
}

// Editry sometimes stores camelCase Builder keys as lowercase (oproduktu, geometriepdf, …).
const BIKE_FIELD_ALIASES = {
  oProduktu: ['oproduktu'],
  oProduktuObrazky: ['oproduktuobrazky'],
  kratkyPopis: ['kratkypopis'],
  geometrieObrazek: ['geometrieobrazek'],
  geometriePdf: ['geometriepdf']
};

function isEmptyFieldValue(value) {
  if (value == null) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
}

// Editry stores tags as [{ tag }] and sizes as [{ velikost }]; pages expect Builder shapes.
const SIZE_FLAGS = { XXXS: 'xxxs', XXS: 'xxs', XS: 'xs', S: 's', M: 'm', L: 'l', XL: 'xl', 'Ď': 'd' };

function normalizeTags(tags) {
  if (!Array.isArray(tags)) {
    return tags;
  }
  return tags.map((entry) => (entry && typeof entry === 'object' ? entry.tag : entry)).filter(Boolean);
}

export function getBikeTags(data) {
  const tags = normalizeTags(data?.tags);
  return Array.isArray(tags) ? tags.map((tag) => String(tag).toUpperCase()) : [];
}

function normalizeVelikosti(velikosti) {
  if (!Array.isArray(velikosti)) {
    return velikosti;
  }
  const flags = {};
  for (const entry of velikosti) {
    const flag = SIZE_FLAGS[String(entry?.velikost || '').toUpperCase()];
    if (flag) {
      flags[flag] = true;
    }
  }
  return flags;
}

// Editry lowercases schema keys, so list items hold jmenospecifikace, jmenobarvy, …
const NESTED_BIKE_KEYS = Object.fromEntries(
  ['nazevKategorie', 'jmenoSpecifikace', 'hodnotaSpecifikace', 'vlastniJmeno', 'jmenoBarvy'].map((key) => [
    key.toLowerCase(),
    key
  ])
);

function camelizeNestedKeys(value) {
  if (Array.isArray(value)) {
    return value.map(camelizeNestedKeys);
  }
  if (!value || typeof value !== 'object') {
    return value;
  }
  const result = {};
  for (const [key, entry] of Object.entries(value)) {
    const canonical = NESTED_BIKE_KEYS[key] || key;
    if (isEmptyFieldValue(result[canonical])) {
      result[canonical] = camelizeNestedKeys(entry);
    }
  }
  return result;
}

function normalizeBikeData(data) {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const normalized = {
    ...data,
    tags: normalizeTags(data.tags),
    velikosti: normalizeVelikosti(data.velikosti),
    specifikace: camelizeNestedKeys(data.specifikace),
    barvy: camelizeNestedKeys(data.barvy)
  };

  for (const [canonical, aliases] of Object.entries(BIKE_FIELD_ALIASES)) {
    if (!isEmptyFieldValue(normalized[canonical])) {
      continue;
    }

    for (const alias of aliases) {
      if (!isEmptyFieldValue(normalized[alias])) {
        normalized[canonical] = normalized[alias];
        break;
      }
    }
  }

  return normalized;
}

// Size tables keep velikosti as [{ nazev, velikost, vyskaPostavy }]; bike normalization would turn it into flags.
const RAW_DATA_MODELS = new Set(['velikostni-tabulky']);

function toBuilderShape(item, model) {
  const raw = getModelEntryData(item);
  const data = RAW_DATA_MODELS.has(model) ? raw : normalizeBikeData(raw);
  const builderId = data.builder_id || String(item.id);

  return {
    id: builderId,
    name: item.name,
    data,
    editryId: item.id,
    slug: item.slug,
    language: item.language
  };
}

export function getBikeField(data, field, fallback = undefined) {
  if (!data || typeof data !== 'object') {
    return fallback;
  }

  if (!isEmptyFieldValue(data[field])) {
    return data[field];
  }

  const aliases = BIKE_FIELD_ALIASES[field] || [];
  for (const alias of aliases) {
    if (!isEmptyFieldValue(data[alias])) {
      return data[alias];
    }
  }

  return fallback;
}

function matchesQuery(entry, query) {
  if (!query) {
    return true;
  }

  const wanted = query.id ?? query['data.id'];
  if (wanted == null) {
    return true;
  }

  return entryMatchesId(entry, wanted);
}

export function entryMatchesId(entry, wanted) {
  if (!entry || wanted == null || wanted === '') {
    return false;
  }

  return (
    String(entry.id) === String(wanted) ||
    String(entry.editryId) === String(wanted) ||
    String(entry.data?.builder_id) === String(wanted)
  );
}

function collectReferenceIds(value, ids) {
  if (value == null || value === '') {
    return;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    ids.add(String(value));
    return;
  }

  if (typeof value !== 'object') {
    return;
  }

  if (value.id != null && value.id !== '') {
    ids.add(String(value.id));
  }
  if (value.editryId != null && value.editryId !== '') {
    ids.add(String(value.editryId));
  }

  collectReferenceIds(value.value, ids);
  collectReferenceIds(value.kategora, ids);
  collectReferenceIds(value.kategoriej, ids);
}

export function getCategoryReferenceIds(kategorie) {
  const ids = new Set();
  const items = Array.isArray(kategorie) ? kategorie : kategorie != null ? [kategorie] : [];

  for (const item of items) {
    collectReferenceIds(item, ids);
  }

  return [...ids];
}

export function bikeBelongsToCategory(bike, category) {
  if (!bike?.data?.kategorie || !category) {
    return false;
  }

  return getCategoryReferenceIds(bike.data.kategorie).some((id) => entryMatchesId(category, id));
}

async function resolveModelId(slug) {
  if (modelIdBySlug.has(slug)) {
    return modelIdBySlug.get(slug);
  }

  const models = await getClient().getPublicModels();
  for (const model of models.data || []) {
    modelIdBySlug.set(model.slug, model.id);
    if (model.name) {
      modelIdBySlug.set(model.name, model.id);
    }
  }

  return modelIdBySlug.get(slug) ?? null;
}

async function loadItems(model, language) {
  const cacheKey = `${model}::${language}`;
  if (itemsCache.has(cacheKey)) {
    return itemsCache.get(cacheKey);
  }

  const request = (async () => {
    const modelId = await resolveModelId(model);
    if (!modelId) {
      return [];
    }

    const response = await getClient().getPublicModelItems(modelId, language);
    return (response.data || []).map((item) => toBuilderShape(item, model));
  })();

  itemsCache.set(cacheKey, request);

  try {
    return await request;
  } catch (error) {
    itemsCache.delete(cacheKey);
    throw error;
  }
}

async function getAllFromEditry(model, options = {}) {
  const language = currentLanguage(options);
  let items = await loadItems(model, language);

  if ((!items || items.length === 0) && language !== 'cs') {
    items = await loadItems(model, 'cs');
  }

  if (options.query) {
    items = items.filter((item) => matchesQuery(item, options.query));
  }

  if (typeof options.limit === 'number') {
    return items.slice(0, options.limit);
  }

  return items;
}

const publishedNewsCache = new Map();

function slugify(value) {
  const slug = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return slug || 'novinka';
}

function newsLanguage(item) {
  const value = item?.language || item?.locale || item?.lang;
  if (!value) {
    return null;
  }
  return toEditryLanguage(value);
}

function bikeReferencesFromNews(reference) {
  const list = Array.isArray(reference) ? reference : reference ? [reference] : [];

  return list
    .filter((entry) => entry && entry.id != null && (!entry.model || entry.model === 'kola'))
    .map((entry) => ({ kolo: { id: String(entry.id) } }));
}

function newsToBuilderShape(item) {
  const title = item.title || '';
  const route = item.slug || `${slugify(title)}-${item.id}`;
  const gallery = Array.isArray(item.gallery) ? item.gallery.filter(Boolean) : [];
  const kola = bikeReferencesFromNews(item.reference);

  return {
    id: `editry-news-${item.id}`,
    name: title,
    data: {
      title,
      description: item.content || item.excerpt || '',
      image: item.image_url || '',
      tag: item.tag || '',
      route,
      galerie: gallery.map((entry) => (typeof entry === 'string' ? { fotka: entry } : entry)),
      video: item.video || '',
      kola,
      kolo: kola[0]?.kolo
    },
    editryNewsId: item.id,
    language: newsLanguage(item),
    publishedAt: item.published_at || item.updated_at || item.created_at || null
  };
}

async function fetchPublishedNews(language) {
  const client = getClient();
  // Package getPublishedNews() does not accept language yet; public API filters via ?language=
  return client.request('GET', '/api/public/news', undefined, {
    auth: 'editryToken',
    query: language ? { language } : undefined
  });
}

async function loadPublishedNewsItems(language) {
  const cacheKey = `published::${language || 'default'}`;
  if (publishedNewsCache.has(cacheKey)) {
    return publishedNewsCache.get(cacheKey);
  }

  const request = (async () => {
    const response = await fetchPublishedNews(language);
    return (response?.data || [])
      .filter((item) => item && item.title && item.is_published !== false)
      .map(newsToBuilderShape)
      .sort((left, right) => {
        const leftTime = left.publishedAt ? Date.parse(left.publishedAt) : 0;
        const rightTime = right.publishedAt ? Date.parse(right.publishedAt) : 0;
        return (Number.isFinite(rightTime) ? rightTime : 0) - (Number.isFinite(leftTime) ? leftTime : 0);
      });
  })();

  publishedNewsCache.set(cacheKey, request);

  try {
    return await request;
  } catch (error) {
    publishedNewsCache.delete(cacheKey);
    throw error;
  }
}

function titleKeyOf(item) {
  return String(item?.data?.title || '').trim().toLowerCase();
}

function mergeNews(published, legacy) {
  const legacyByTitle = new Map();

  for (const item of legacy) {
    const titleKey = titleKeyOf(item);
    if (titleKey && !legacyByTitle.has(titleKey)) {
      legacyByTitle.set(titleKey, item);
    }
  }

  const seen = new Set();
  const merged = [];

  for (const item of published) {
    const titleKey = titleKeyOf(item);
    if (!titleKey || titleKey === 'ignore' || seen.has(titleKey)) {
      continue;
    }

    const existing = legacyByTitle.get(titleKey);
    if (existing?.data?.route) {
      item.data.route = existing.data.route;
    }

    seen.add(titleKey);
    merged.push(item);
  }

  for (const item of legacy) {
    const titleKey = titleKeyOf(item);
    if (!titleKey || titleKey === 'ignore' || seen.has(titleKey)) {
      continue;
    }

    seen.add(titleKey);
    merged.push(item);
  }

  return merged;
}

async function getAllNovinky(options = {}) {
  const legacyOptions = { ...options };
  delete legacyOptions.limit;
  delete legacyOptions.query;

  const legacyPromise = (
    isEditryConfigured()
      ? getAllFromEditry('novinky', legacyOptions)
      : getAllFromBuilder('novinky', legacyOptions)
  ).catch((error) => {
    console.error('Error fetching legacy novinky:', error);
    return [];
  });

  const publishedPromise = isEditryConfigured()
    ? (async () => {
        const language = currentLanguage(options);
        let items = await loadPublishedNewsItems(language);
        if ((!items || items.length === 0) && language !== 'cs') {
          items = await loadPublishedNewsItems('cs');
        }
        return items;
      })().catch((error) => {
        console.error('Error fetching Editry news:', error);
        return [];
      })
    : Promise.resolve([]);

  const [legacy, published] = await Promise.all([legacyPromise, publishedPromise]);
  let items = mergeNews(published, Array.isArray(legacy) ? legacy : []);

  if (options.query) {
    items = items.filter((item) => matchesQuery(item, options.query));
  }

  if (typeof options.limit === 'number') {
    return items.slice(0, options.limit);
  }

  return items;
}

async function getFromEditry(model, options = {}) {
  const items = await getAllFromEditry(model, options);
  return items.find((item) => matchesQuery(item, options.query)) || items[0] || null;
}

async function getFromBuilder(model, options = {}) {
  const locale = currentLocale(options);
  const requestOptions = { ...options, locale };
  const result = await originalBuilder.get(model, requestOptions);
  if (!result && locale !== 'cs-CZ') {
    return originalBuilder.get(model, { ...options, locale: 'cs-CZ' });
  }
  return result;
}

async function getAllFromBuilder(model, options = {}) {
  const locale = currentLocale(options);
  const result = await originalBuilder.getAll(model, { ...options, locale });
  if ((!result || result.length === 0) && locale !== 'cs-CZ') {
    return originalBuilder.getAll(model, { ...options, locale: 'cs-CZ' });
  }
  return result;
}

export const builder = {
  apiKey: originalBuilder.apiKey,
  init(apiKey = BUILDER_PUBLIC_KEY) {
    originalBuilder.init(apiKey);
    this.apiKey = apiKey;
    return this;
  },
  async get(model, options = {}) {
    if (model === 'novinky') {
      const items = await getAllNovinky(options);
      return items.find((item) => matchesQuery(item, options.query)) || items[0] || null;
    }
    if (isEditryConfigured()) {
      return getFromEditry(model, options);
    }
    return getFromBuilder(model, options);
  },
  async getAll(model, options = {}) {
    if (model === 'novinky') {
      return getAllNovinky(options);
    }
    if (isEditryConfigured()) {
      return getAllFromEditry(model, options);
    }
    return getAllFromBuilder(model, options);
  }
};

export function preloadPublicCmsData() {
  if (!isEditryConfigured()) {
    return Promise.resolve();
  }

  return Promise.all([
    getAllFromEditry('home-kola'),
    getAllNovinky(),
    getAllFromEditry('pick-4'),
    getAllFromEditry('header'),
    getAllFromEditry('footer')
  ]);
}
